import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Loader2, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "@/components/Reveal";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function formatDateLabel(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return {
    weekday: d.toLocaleDateString(undefined, { weekday: "short" }),
    day: d.getDate(),
    month: d.toLocaleDateString(undefined, { month: "short" }),
  };
}

export default function Schedule() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", session_id: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!loading && location.hash === "#interest-form") {
      setTimeout(() => document.getElementById("interest-form")?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [loading, location.hash]);

  useEffect(() => {
    api
      .get("/classes/schedule")
      .then(({ data }) => setSessions(data))
      .catch((err) => toast.error(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: user.name || "", email: user.email || "", phone: user.phone || "" }));
    }
  }, [user]);

  const dates = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const all = [...new Set(sessions.map((s) => s.date))].sort();
    const upcoming = all.filter((d) => d >= today);
    return (upcoming.length ? upcoming : all).slice(0, 7);
  }, [sessions]);
  const byDate = useMemo(() => {
    const map = {};
    for (const s of sessions) {
      (map[s.date] = map[s.date] || []).push(s);
    }
    for (const d of Object.keys(map)) map[d].sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [sessions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.session_id) {
      toast.error("Pick a class from the list.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/classes/interest", form);
      toast.success(data.message);
      setForm((f) => ({ ...f, session_id: "", notes: "" }));
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-28 sm:pt-36 pb-20 sm:pb-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="font-accent italic text-gold text-lg mb-3">The week at Elysium</p>
        <h1 className="font-anton uppercase tracking-tighter text-cream text-4xl sm:text-6xl">The Weekly Schedule</h1>
        <p className="text-smoke mt-4 max-w-xl">
          Here's what's on this week. See a class you fancy? Register your interest below and the team will be in
          touch to get you booked in — nothing to pay online.
        </p>
        <Button
          asChild
          className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider mt-6"
          data-testid="register-interest-cta"
        >
          <a href="#interest-form">
            Register Interest <ArrowDown className="h-4 w-4 ml-2" />
          </a>
        </Button>
      </Reveal>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 text-gold animate-spin" />
        </div>
      ) : (
        <div
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 select-none"
          data-testid="schedule-static-grid"
        >
          {dates.map((date) => {
            const { weekday, day, month } = formatDateLabel(date);
            return (
              <div key={date} className="border border-line bg-surface">
                <div className="border-b border-line px-4 py-3 text-center">
                  <p className="text-[10px] uppercase tracking-widest text-smoke">{weekday}</p>
                  <p className="font-anton text-gold text-2xl leading-tight">{day}</p>
                  <p className="text-[10px] uppercase tracking-widest text-smoke">{month}</p>
                </div>
                <div className="p-3 space-y-2">
                  {(byDate[date] || []).map((s) => (
                    <div key={s.id} className="border border-line/60 bg-void/40 p-3" data-testid={`schedule-session-${s.id}`}>
                      <p className="flex items-center gap-1.5 text-gold font-anton text-lg">
                        <Clock className="h-3.5 w-3.5" /> {s.start_time}
                      </p>
                      <p className="text-cream text-sm font-semibold leading-snug mt-1">{s.class_type}</p>
                      <p className="text-smoke text-xs mt-0.5">
                        Coach {s.coach} · {s.duration_minutes} min
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div id="interest-form" className="mt-16 sm:mt-24 max-w-2xl mx-auto scroll-mt-28">
        <Reveal>
          <div className="border border-gold bg-gold/5 p-6 sm:p-10">
            <p className="font-accent italic text-gold text-lg mb-2">Want in?</p>
            <h2 className="font-anton uppercase tracking-tighter text-cream text-3xl">Register your interest</h2>
            <p className="text-smoke mt-3 text-sm">
              Tell us who you are and which class you're eyeing — the team will be in touch to confirm your spot and
              sort everything directly with you.
            </p>
            <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="class-interest-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-widest text-smoke">Name</label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none bg-void border-line text-cream mt-2 h-12 focus-visible:ring-gold focus-visible:border-gold"
                    data-testid="interest-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-smoke">Phone</label>
                  <Input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="rounded-none bg-void border-line text-cream mt-2 h-12 focus-visible:ring-gold focus-visible:border-gold"
                    data-testid="interest-phone-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-smoke">Email</label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-none bg-void border-line text-cream mt-2 h-12 focus-visible:ring-gold focus-visible:border-gold"
                  data-testid="interest-email-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-smoke">Which class?</label>
                <select
                  required
                  value={form.session_id}
                  onChange={(e) => setForm({ ...form, session_id: e.target.value })}
                  className="w-full mt-2 h-12 px-3 bg-void border border-line text-cream focus:outline-none focus:border-gold"
                  data-testid="interest-class-select"
                >
                  <option value="">Pick a class…</option>
                  {dates.map((date) => {
                    const { weekday, day, month } = formatDateLabel(date);
                    return (byDate[date] || []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {weekday} {day} {month} — {s.start_time} {s.class_type}
                      </option>
                    ));
                  })}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-smoke">Anything we should know? (optional)</label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="rounded-none bg-void border-line text-cream mt-2 focus-visible:ring-gold focus-visible:border-gold"
                  data-testid="interest-notes-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider w-full h-12"
                data-testid="interest-submit-button"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Register My Interest"}
              </Button>
            </form>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
